// Вкладка «Акты и закрытие» (Stage 6, контракт §8–§9):
//   - панель финансовых итогов с backend (оплачено / долг / закрыто актами /
//     осталось закрыть — раздельные поля summary/checklist);
//   - список актов заявки со строками (act.lines либо /finance/act-line/?act=);
//   - создание частичного акта: выбор invoice_line из выпадающего списка
//     (счёт/позиция/сумма/остаток) без ручного ввода UUID, сумма ≤ остатка;
//   - подтверждение акта; отмена акта с обязательной причиной;
//   - подпись/оригинал по issued-актам (FinancialDelivery, «внесено вручную»);
//   - чек-лист закрытия с кодами блокеров; policy_not_defined показан явно;
//   - «Закрыть заявку» (accountant/supervisor/admin) и «Повторно открыть»
//     (supervisor/admin, обязательная причина).
// Все суммы и готовность — только с backend; единственное клиентское
// вычисление — остаток строки счёта для подсказки в форме (amount − Σ issued
// act lines, данные backend), сервер всё равно проверяет лимит жёстко (§3).
import { useState, type FormEvent } from 'react';
import { ApiError } from '../../api/client';
import { finance } from '../../api/endpoints';
import {
    cancelAct, closeRequest, confirmAct, createActDraft, createDeliveryDraft,
    exemptDeliveryOriginal, getClosureChecklist, getClosureSummary, listActLines,
    listActs, listFinancialDeliveries, listInvoiceLines, markDelivery,
    reopenRequest
} from '../../api/finance-closure';
import type {
    Act, ActLine, ClosureBlocker, ClosureChecklist, ClosureFinance,
    FinancialDelivery, Invoice, InvoiceLine, RequestClosureSummary,
} from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import {
    Card, Empty, ErrorBox, Field, fmtDate, fmtMoney, KV, label, Loading,
    shortId, StatusBadge
} from '../../components/ui';
import { useAsync } from '../../hooks';
import type { CardData } from '../RequestCardPage';

// ---------- Тексты ----------

const BLOCKER_TEXT: Record<string, string> = {
    policy_not_defined: 'Политика закрытия не определена',
    items_incomplete: 'Не все позиции завершены',
    missing_result: 'По позиции нет выданного документа-результата',
    unpaid_debt: 'Неоплаченный долг по заявке',
    unclosed_acts: 'Выставленная сумма не закрыта актами полностью',
    signature_required: 'Требуется подпись (ЭДО) по выставленному акту',
    original_required: 'Требуется получение оригинала акта',
    already_closed: 'Заявка уже закрыта',
};

const EDO_OPTIONS = ['', 'sent', 'signed', 'rejected'];
const ORIGINAL_OPTIONS = ['', 'sent', 'received', 'lost'];

function blockerText(code: string): string {
    return BLOCKER_TEXT[code] ?? code;
}

function isMissingEndpoint(e: unknown): boolean {
    return e instanceof ApiError && (e.status === 404 || e.status === 405);
}

function num(value?: string | null): number {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
}

// ---------- Загрузка данных ----------

interface ClosureData {
    summary: RequestClosureSummary | null;
    checklist: ClosureChecklist | null;
    contractMissing: boolean;
    acts: Act[];
    actLines: ActLine[];
    invoiceLines: InvoiceLine[];
    invoices: Invoice[];
    deliveries: FinancialDelivery[];
}

function useClosureData(data: CardData, tick: number) {
    const itemKey = data.items.map((i) => i.id).join(',');
    return useAsync<ClosureData>(async () => {
        const itemIds = new Set(data.items.map((i) => i.id));
        const requestId = data.request.id;

        // Контрактные endpoints §8 могут быть ещё не реализованы на backend —
        // 404/405 не блокируют отображение списка актов (GET — существующие).
        let contractMissing = false;
        const contractCall = async <T,>(fn: () => Promise<T>): Promise<T | null> => {
            try {
                return await fn();
            } catch (e) {
                if (isMissingEndpoint(e)) { contractMissing = true; return null; }
                throw e;
            }
        };

        const [summary, checklist] = await Promise.all([
            contractCall(() => getClosureSummary(requestId)),
            contractCall(() => getClosureChecklist(requestId)),
        ]);

        // Строки счетов заявки: фильтр ?item=<id> поддержан точными фильтрами
        // backend; на всякий случай дополнительно фильтруем на клиенте.
        const lineLists = await Promise.all(
            data.items.map((it) => listInvoiceLines({ item: it.id }).catch(() => [] as InvoiceLine[])));
        let invoiceLines = lineLists.flat().filter((l) => !l.item || itemIds.has(l.item));
        if (!invoiceLines.length) {
            invoiceLines = (await listInvoiceLines().catch(() => [] as InvoiceLine[]))
                .filter((l) => l.item && itemIds.has(l.item));
        }
        const lineIds = new Set(invoiceLines.map((l) => l.id));

        const invoiceIds = new Set(invoiceLines.map((l) => l.invoice));
        const invoices = (await finance.invoices().catch(() => [] as Invoice[]))
            .filter((i) => invoiceIds.has(i.id));

        // Строки актов: заявка определяется через invoice_line → item → request.
        const allActLines = await listActLines().catch(() => [] as ActLine[]);
        const actLines = allActLines.filter(
            (l) => (l.invoice_line && lineIds.has(l.invoice_line)) || (l.item && itemIds.has(l.item)));

        const actIds = new Set(actLines.map((l) => l.act));
        // Акты из summary — авторитетный источник списка актов заявки (§8).
        for (const a of summary?.acts ?? []) actIds.add(a.id);

        const actsAll = await listActs().catch(() => [] as Act[]);
        const actsById = new Map(actsAll.map((a) => [a.id, a]));
        // summary.acts — усечённая проекция (без revision; строки с ключами
        // invoice_line_id/item_id): нормализуем и дополняем только то, чего
        // нет в полном сериализаторе акта (revision критичен для команд).
        for (const raw of summary?.acts ?? []) {
            const a = raw as Act & { delivery?: FinancialDelivery | null };
            const full = actsById.get(a.id);
            const lines: ActLine[] = (a.lines ?? []).map((l) => ({
                ...l,
                act: a.id,
                invoice_line: l.invoice_line
                    ?? (l as unknown as Record<string, unknown>).invoice_line_id as string | null
                    ?? null,
                item: l.item
                    ?? (l as unknown as Record<string, unknown>).item_id as string | null
                    ?? null,
            }));
            actsById.set(a.id, { ...a, ...(full ?? {}), lines });
            if (a.delivery && !a.delivery.act) a.delivery.act = a.id;
        }
        const acts = [...actIds].map((id) => actsById.get(id)).filter((a): a is Act => Boolean(a));

        // Вложенные строки из сериализатора/summary акта — дополняем act-линии.
        const seenLine = new Set(actLines.map((l) => l.id));
        for (const a of acts) {
            for (const l of a.lines ?? []) {
                if (!seenLine.has(l.id)) { actLines.push(l); seenLine.add(l.id); }
            }
        }

        const actIdSet = new Set(acts.map((a) => a.id));
        const deliveries = (await listFinancialDeliveries().catch(() => [] as FinancialDelivery[]))
            .filter((d) => d.act && actIdSet.has(d.act));
        const deliveryIds = new Set(deliveries.map((d) => d.id));
        for (const raw of summary?.acts ?? []) {
            const a = raw as Act & { delivery?: FinancialDelivery | null };
            if (a.delivery?.id && !deliveryIds.has(a.delivery.id)) {
                deliveries.push({ ...a.delivery, act: a.id });
                deliveryIds.add(a.delivery.id);
            }
        }

        return { summary, checklist, contractMissing, acts, actLines, invoiceLines, invoices, deliveries };
    }, [data.request.id, itemKey, tick]);
}

/** Финансовые агрегаты: checklist.finance (§6) → summary.finance → верхний
    уровень summary (запасные имена). */
function financeOf(d: ClosureData): ClosureFinance {
    if (d.checklist?.finance) return d.checklist.finance;
    if (d.summary?.finance) return d.summary.finance;
    const s = d.summary ?? {};
    return {
        invoiced: typeof s.invoiced === 'string' ? s.invoiced : undefined,
        paid_net: typeof s.paid_net === 'string' ? s.paid_net : undefined,
        debt: typeof s.debt === 'string' ? s.debt : undefined,
        closed_by_acts: d.summary?.closed_by_acts,
        remaining_to_close: d.summary?.remaining_to_close,
        currency: typeof s.currency === 'string' ? s.currency : undefined,
        // Fix1 (F1-01): мультивалютная разбивка — пробрасываем как есть.
        currencies: Array.isArray(s.currencies) ? s.currencies as string[] : undefined,
        by_currency: (s.by_currency ?? undefined) as ClosureFinance['by_currency'],
        mixed_currencies: Boolean(s.mixed_currencies),
    };
}

// ---------- Общие мелкие формы ----------

function ReasonForm({ title, label: fieldLabel, submitLabel, onSubmit, onClose }: {
    title: string;
    label: string;
    submitLabel: string;
    onSubmit: (reason: string) => Promise<void>;
    onClose: () => void;
}) {
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState<unknown>(null);

    const submit = async (e: FormEvent) => {
        e.preventDefault();
        setBusy(true); setErr(null);
        try { await onSubmit(reason.trim()); onClose(); }
        catch (x) { setErr(x); } finally { setBusy(false); }
    };

    return (
        <form className="inline-form" onSubmit={submit}>
            <ErrorBox error={err} />
            <div className="form-row">
                <Field label={fieldLabel} hint="Причина обязательна">
                    <input value={reason} onChange={(e) => setReason(e.target.value)}
                           required placeholder={title} />
                </Field>
            </div>
            <div className="form-actions">
                <button className="btn danger" disabled={busy || !reason.trim()}>
                    {busy ? 'Выполнение…' : submitLabel}
                </button>
                <button type="button" className="btn" onClick={onClose}>Отмена</button>
            </div>
        </form>
    );
}

// ---------- Форма фиксации подписи/оригинала ----------

function MarkDeliveryForm({ delivery, onDone }: {
    delivery: FinancialDelivery;
    onDone: () => void;
}) {
    const [edo, setEdo] = useState(delivery.edo_status ?? '');
    const [orig, setOrig] = useState(delivery.original_status ?? '');
    const [origReq, setOrigReq] = useState(Boolean(delivery.original_required));
    const [basis, setBasis] = useState('');
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState<unknown>(null);

    const changed = edo !== (delivery.edo_status ?? '')
        || orig !== (delivery.original_status ?? '')
        || origReq !== Boolean(delivery.original_required);

    const submit = async (e: FormEvent) => {
        e.preventDefault();
        setBusy(true); setErr(null);
        try {
            await markDelivery(delivery.id, {
                revision: delivery.revision,
                edo_status: edo,
                original_status: orig,
                original_required: origReq,
                basis: basis.trim(),
            });
            onDone();
        } catch (x) { setErr(x); } finally { setBusy(false); }
    };

    return (
        <form className="inline-form" onSubmit={submit} data-testid="mark-delivery-form">
            <ErrorBox error={err} />
            <div className="form-row">
                <Field label="Статус подписи/ЭДО">
                    <select value={edo} onChange={(e) => setEdo(e.target.value)}>
                        {EDO_OPTIONS.map((s) => <option key={s} value={s}>{s ? label(s) : '— не задан —'}</option>)}
                    </select>
                </Field>
                <Field label="Оригинал обязателен">
                    <select value={origReq ? 'yes' : 'no'} onChange={(e) => setOrigReq(e.target.value === 'yes')}>
                        <option value="no">Нет</option>
                        <option value="yes">Да</option>
                    </select>
                </Field>
                <Field label="Статус оригинала">
                    <select value={orig} onChange={(e) => setOrig(e.target.value)}>
                        {ORIGINAL_OPTIONS.map((s) => <option key={s} value={s}>{s ? label(s) : '— не задан —'}</option>)}
                    </select>
                </Field>
                <Field label="Основание" hint="Обязательно при смене статуса">
                    <input value={basis} onChange={(e) => setBasis(e.target.value)} required
                           placeholder="На основании чего фиксируется статус" />
                </Field>
            </div>
            <div className="form-actions">
                <button className="btn primary" disabled={busy || !changed || !basis.trim()}>
                    {busy ? 'Сохранение…' : 'Сохранить статус'}
                </button>
            </div>
        </form>
    );
}

// ---------- Освобождение от обязательного оригинала (Fix1 F1-02) ----------

function ExemptOriginalForm({ delivery, onDone }: {
    delivery: FinancialDelivery;
    onDone: () => void;
}) {
    const [basis, setBasis] = useState('');
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState<unknown>(null);

    const submit = async (e: FormEvent) => {
        e.preventDefault();
        setBusy(true); setErr(null);
        try {
            await exemptDeliveryOriginal(delivery.id, {
                revision: delivery.revision,
                basis: basis.trim(),
            });
            onDone();
        } catch (x) { setErr(x); } finally { setBusy(false); }
    };

    return (
        <form className="inline-form" onSubmit={submit} data-testid="exempt-original-form">
            <ErrorBox error={err} />
            <div className="form-row">
                <Field label="Основание освобождения" hint="Обязательно">
                    <input value={basis} onChange={(e) => setBasis(e.target.value)} required
                           placeholder="На основании чего оригинал не требуется" />
                </Field>
            </div>
            <div className="form-actions">
                <button className="btn primary" disabled={busy || !basis.trim()}>
                    {busy ? 'Выполнение…' : 'Освободить от оригинала'}
                </button>
            </div>
        </form>
    );
}

function DeliveryBlock({ act, delivery, canManage, canExempt, requireOriginal, onChanged }: {
    act: Act;
    delivery: FinancialDelivery | undefined;
    canManage: boolean;
    /** supervisor/admin (F1-02): освобождение от обязательного оригинала. */
    canExempt: boolean;
    /** Активная политика требует оригинал (require_original). */
    requireOriginal: boolean;
    onChanged: () => void;
}) {
    const [markOpen, setMarkOpen] = useState(false);
    const [exemptOpen, setExemptOpen] = useState(false);
    const [ensuring, setEnsuring] = useState(false);
    const [err, setErr] = useState<unknown>(null);

    const ensureAndOpen = async () => {
        if (delivery) { setMarkOpen(true); return; }
        setEnsuring(true); setErr(null);
        try {
            await createDeliveryDraft({ act_id: act.id });
            onChanged();
            setMarkOpen(true);
        } catch (x) { setErr(x); } finally { setEnsuring(false); }
    };

    // Освобождение имеет смысл, пока оригинал не получен и не освобождён.
    const exemptApplies = Boolean(
        canExempt && requireOriginal && delivery
        && !delivery.original_exempt
        && delivery.original_status !== 'received');

    return (
        <div className="section" data-testid={`delivery-${act.id}`}>
            <div className="kv-grid" style={{ marginBottom: 6 }}>
                <KV k="Подпись/ЭДО" v={<StatusBadge value={delivery?.edo_status || null} />} />
                <KV k="Оригинал обязателен" v={delivery?.original_required ? 'Да' : 'Нет'} />
                <KV k="Статус оригинала" v={<StatusBadge value={delivery?.original_status || null} />} />
                {delivery?.status_source === 'manual' && (
                    <KV k="Источник" v={<span className="badge warn">внесено вручную</span>} />
                )}
                {delivery?.basis ? <KV k="Основание" v={delivery.basis} /> : null}
                {delivery?.marked_at ? <KV k="Зафиксировано" v={fmtDate(delivery.marked_at)} /> : null}
                {delivery?.original_exempt ? (
                    <KV k="Оригинал" v={<span className="badge ok" data-testid="original-exempt">освобождён</span>} />
                ) : null}
                {delivery?.original_exempt_basis ? (
                    <KV k="Основание освобождения" v={delivery.original_exempt_basis} />
                ) : null}
                {delivery?.original_exempt_at ? (
                    <KV k="Освобождён" v={fmtDate(delivery.original_exempt_at)} />
                ) : null}
            </div>
            <ErrorBox error={err} />
            {canManage && !markOpen && (
                <button className="btn small" onClick={() => void ensureAndOpen()} disabled={ensuring}>
                    {ensuring ? 'Подготовка…' : 'Зафиксировать статус'}
                </button>
            )}
            {exemptApplies && !exemptOpen && (
                <button className="btn small" data-testid="exempt-original-btn"
                        onClick={() => setExemptOpen(true)}>
                    Освободить от оригинала
                </button>
            )}
            {exemptOpen && delivery && (
                <ExemptOriginalForm delivery={delivery}
                                    onDone={() => { setExemptOpen(false); onChanged(); }} />
            )}
            {markOpen && delivery && (
                <MarkDeliveryForm delivery={delivery} onDone={() => { setMarkOpen(false); onChanged(); }} />
            )}
        </div>
    );
}

// ---------- Форма нового акта ----------

function NewActForm({ invoiceLines, invoices, items, closedByLine, onDone }: {
    invoiceLines: InvoiceLine[];
    invoices: Invoice[];
    items: CardData['items'];
    closedByLine: Map<string, number>;
    onDone: () => void;
}) {
    const [lineId, setLineId] = useState('');
    const [amount, setAmount] = useState('');
    const [number, setNumber] = useState(() => `ACT-${Date.now().toString(36).toUpperCase()}`);
    const [actDate, setActDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState<unknown>(null);
    const [created, setCreated] = useState('');

    const invoiceById = new Map(invoices.map((i) => [i.id, i]));
    const itemById = new Map(items.map((i) => [i.id, i]));
    const line = invoiceLines.find((l) => l.id === lineId);
    const invoice = line ? invoiceById.get(line.invoice) : undefined;
    const remaining = line ? num(line.amount) - (closedByLine.get(line.id) ?? 0) : null;
    const amountNum = Number(amount);
    const amountInvalid = !amount || Number.isNaN(amountNum) || amountNum <= 0
        || (remaining !== null && remaining >= 0 && amountNum > remaining + 1e-9);

    const submit = async (e: FormEvent) => {
        e.preventDefault();
        if (!line || !invoice) return;
        setBusy(true); setErr(null); setCreated('');
        try {
            await createActDraft({
                issuer_id: String(invoice.issuer),
                payer_id: String(invoice.payer ?? ''),
                number: number.trim(),
                date: actDate,
                currency: invoice.currency ?? 'RUB',
                lines: [{ invoice_line_id: line.id, amount }],
            });
            setCreated(number.trim());
            setAmount(''); setLineId('');
            setNumber(`ACT-${Date.now().toString(36).toUpperCase()}`);
            onDone();
        } catch (x) { setErr(x); } finally { setBusy(false); }
    };

    return (
        <form className="inline-form" onSubmit={submit} data-testid="new-act-form">
            <ErrorBox error={err} />
            {created && <div className="alert alert-info">Черновик акта {created} создан — подтвердите его в списке актов.</div>}
            <div className="form-row">
                <Field label="Строка счёта" hint="Счёт · позиция · сумма · остаток к закрытию">
                    <select value={lineId} onChange={(e) => setLineId(e.target.value)} required>
                        <option value="">— выберите строку счёта —</option>
                        {invoiceLines.map((l) => {
                            const inv = invoiceById.get(l.invoice);
                            const item = l.item ? itemById.get(l.item) : undefined;
                            const rem = num(l.amount) - (closedByLine.get(l.id) ?? 0);
                            return (
                                <option key={l.id} value={l.id}>
                                    {`Счёт ${inv?.number ?? shortId(l.invoice)} · ${item?.title ?? 'позиция'} · ${fmtMoney(l.amount, inv?.currency)} · остаток ${fmtMoney(rem, inv?.currency)}`}
                                </option>
                            );
                        })}
                    </select>
                </Field>
                <Field label="Сумма по акту" hint={remaining !== null ? `Не больше остатка ${fmtMoney(remaining, invoice?.currency)}` : undefined}>
                    <input type="number" step="0.01" min="0.01" value={amount}
                           onChange={(e) => setAmount(e.target.value)} required />
                </Field>
            </div>
            <div className="form-row">
                <Field label="Номер акта"><input value={number} onChange={(e) => setNumber(e.target.value)} required /></Field>
                <Field label="Дата акта"><input type="date" value={actDate} onChange={(e) => setActDate(e.target.value)} required /></Field>
            </div>
            <div className="form-actions">
                <button className="btn primary" disabled={busy || !line || amountInvalid || !number.trim()}>
                    {busy ? 'Создание…' : 'Создать черновик акта'}
                </button>
            </div>
        </form>
    );
}

// ---------- Чек-лист ----------

function ChecklistBlock({ checklist }: { checklist: ClosureChecklist | null }) {
    if (!checklist) {
        return <Empty text="Чек-лист закрытия недоступен (endpoint не реализован на backend)." />;
    }
    const policy = checklist.policy;
    const defined = Boolean(policy && policy.defined !== false);
    const blockers = checklist.blockers ?? [];
    return (
        <div>
            <div className="kv-grid" style={{ marginBottom: 8 }}>
                <KV k="Стадия заявки" v={<StatusBadge value={checklist.stage} />} />
                <KV k="Закрыта" v={checklist.closed ? 'Да' : 'Нет'} />
                <KV k="Политика" v={defined
                    ? `v${policy?.version ?? '—'} · результат:${policy?.require_result ? 'да' : 'нет'} · акты:${policy?.require_acts ? 'да' : 'нет'} · подпись:${policy?.require_signature ? 'да' : 'нет'} · оригинал:${policy?.require_original ? 'да' : 'нет'}`
                    : 'не определена'} />
            </div>
            {!defined && (
                <div className="alert alert-warn" data-testid="policy-not-defined">
                    <strong>Политика закрытия не определена.</strong>
                    <span>Активная ClosurePolicy отсутствует — закрытие невозможно до её установки (supervisor/admin).</span>
                </div>
            )}
            {blockers.length === 0 ? (
                <div className="alert alert-info" data-testid="closure-blockers-empty">
                    Блокеров нет — заявку можно закрыть.
                </div>
            ) : (
                <ul data-testid="closure-blockers" style={{ margin: '4px 0', paddingLeft: 18 }}>
                    {blockers.map((b: ClosureBlocker, i) => (
                        <li key={`${b.code}-${i}`} data-blocker={b.code} style={{ marginBottom: 4 }}>
                            <span className="badge bad">{blockerText(b.code)}</span>{' '}
                            {b.message ? <span className="muted">{b.message}</span> : null}
                            {(b.refs ?? []).length > 0 && (
                                <span className="muted"> · refs: {(b.refs ?? []).map((r) => (
                                    <code key={JSON.stringify(r)} style={{ marginRight: 4 }}>
                                        {typeof r === 'string' ? shortId(r) : JSON.stringify(r)}
                                    </code>
                                ))}</span>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

// ---------- Основная вкладка ----------

export function ActsClosureTab({ data }: { data: CardData }) {
    const { user } = useAuth();
    const [tick, setTick] = useState(0);
    const d = useClosureData(data, tick);
    const reload = () => setTick((t) => t + 1);

    const [cancelFor, setCancelFor] = useState<Act | null>(null);
    const [reopenOpen, setReopenOpen] = useState(false);
    const [actionErr, setActionErr] = useState<unknown>(null);
    const [actionBusy, setActionBusy] = useState(false);
    const [notice, setNotice] = useState('');

    const roles = new Set(user?.roles ?? []);
    const isAdmin = Boolean(user?.is_staff || user?.is_superuser) || roles.has('admin');
    const canManage = isAdmin || ['accountant', 'supervisor', 'admin'].some((r) => roles.has(r));
    const canReopen = isAdmin || ['supervisor', 'admin'].some((r) => roles.has(r));

    if (d.error && !d.data) return <ErrorBox error={d.error} onRetry={reload} />;
    if (!d.data) return <Loading text="Загрузка данных закрытия…" />;
    const cd = d.data;

    const fin = financeOf(cd);
    const itemById = new Map(data.items.map((i) => [i.id, i]));
    const invoiceById = new Map(cd.invoices.map((i) => [i.id, i]));
    const deliveryByAct = new Map(cd.deliveries.map((x) => [x.act!, x]));

    // Остаток строки счёта для подсказки формы: amount − Σ issued act lines
    // (все исходные данные — backend; лимит сервер проверяет жёстко, §3).
    const closedByLine = new Map<string, number>();
    const actStatus = new Map(cd.acts.map((a) => [a.id, a.status ?? 'draft']));
    for (const l of cd.actLines) {
        if (actStatus.get(l.act) === 'issued' && l.invoice_line) {
            closedByLine.set(l.invoice_line, (closedByLine.get(l.invoice_line) ?? 0) + num(l.amount));
        }
    }

    const linesOfAct = (actId: string) => cd.actLines.filter((l) => l.act === actId);

    const run = async (fn: () => Promise<unknown>, okText = '') => {
        setActionBusy(true); setActionErr(null); setNotice('');
        try {
            await fn();
            if (okText) setNotice(okText);
            reload();
        } catch (x) { setActionErr(x); } finally { setActionBusy(false); }
    };

    const closed = Boolean(cd.checklist?.closed) || data.request.stage === 'success';

    // Активная политика требует оригинал → действие «Освободить от оригинала»
    // (F1-02, только supervisor/admin — canReopen покрывает тот же набор ролей).
    const activePolicy = cd.checklist?.policy;
    const requireOriginal = Boolean(
        activePolicy && activePolicy.defined !== false && activePolicy.require_original);

    // Fix1 (F1-01): список валют и признак мультивалютной заявки.
    const finCurrencies = fin.currencies?.length
        ? fin.currencies
        : Object.keys(fin.by_currency ?? {});
    const multiCurrency = Boolean(fin.mixed_currencies) || finCurrencies.length > 1;

    return (
        <div>
            {cd.contractMissing && (
                <div className="alert alert-warn">
                    <strong>Контрактные endpoints Stage 6 недоступны.</strong>
                    <span>Backend вернул 404/405 для request-closure/act-команд — доступны только чтение списков.</span>
                </div>
            )}
            <ErrorBox error={actionErr} />
            {notice && <div className="alert alert-info">{notice}</div>}

            <Card title="Финансовые итоги" actions={<button className="btn small" onClick={reload}>Обновить</button>}>
                {multiCurrency && (
                    <div className="alert alert-info" data-testid="closure-finance-mixed"
                         style={{ marginBottom: 8 }}>
                        Несколько валют — итоги показаны по каждой валюте отдельно,
                        без суммирования разных валют.
                    </div>
                )}
                {!multiCurrency ? (
                    <div className="kv-grid" data-testid="closure-finance">
                        <KV k="Выставлено" v={fmtMoney(fin.invoiced, fin.currency)} />
                        <KV k="Оплачено (нетто)" v={fmtMoney(fin.paid_net, fin.currency)} />
                        <KV k="Долг" v={fmtMoney(fin.debt, fin.currency)} />
                        <KV k="Закрыто актами" v={fmtMoney(fin.closed_by_acts, fin.currency)} />
                        <KV k="Осталось закрыть" v={fmtMoney(fin.remaining_to_close, fin.currency)} />
                        <KV k="Валюта" v={fin.currency ?? '—'} />
                    </div>
                ) : (
                    <div data-testid="closure-finance">
                        {finCurrencies.map((cur) => {
                            const t = fin.by_currency?.[cur] ?? {};
                            return (
                                <div key={cur} data-currency={cur} style={{ marginBottom: 10 }}>
                                    <div className="badge" style={{ marginBottom: 4 }}>{cur}</div>
                                    <div className="kv-grid">
                                        <KV k="Выставлено" v={fmtMoney(t.invoiced, cur)} />
                                        <KV k="Оплачено (нетто)" v={fmtMoney(t.paid_net, cur)} />
                                        <KV k="Долг" v={fmtMoney(t.debt, cur)} />
                                        <KV k="Закрыто актами" v={fmtMoney(t.closed_by_acts, cur)} />
                                        <KV k="Осталось закрыть" v={fmtMoney(t.remaining_to_close, cur)} />
                                    </div>
                                </div>
                            );
                        })}
                        {finCurrencies.length === 0 && (
                            <Empty text="Нет данных по валютам." />
                        )}
                    </div>
                )}
            </Card>

            <Card title="Акты заявки">
                {cd.acts.length === 0 ? (
                    <Empty text="Актов по заявке нет" />
                ) : (
                    <div className="table-wrap" data-testid="closure-acts">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Номер</th><th>Дата</th><th>Статус</th><th>Сумма</th>
                                    <th>Строки / доставка</th><th>Действия</th>
                                </tr>
                            </thead>
                            <tbody>
                                {cd.acts.map((a) => (
                                    <tr key={a.id} data-act={a.id}>
                                        <td>{a.number ?? shortId(a.id)}</td>
                                        <td>{a.date ?? fmtDate(a.created_at)}</td>
                                        <td>
                                            <StatusBadge value={a.status} />
                                            {a.status === 'cancelled' && a.cancellation_reason ? (
                                                <div className="muted" style={{ fontSize: 12 }}>
                                                    Причина: {a.cancellation_reason}
                                                </div>
                                            ) : null}
                                        </td>
                                        <td className="nowrap">{fmtMoney(a.total, a.currency)}</td>
                                        <td>
                                            <ul style={{ margin: 0, paddingLeft: 16 }}>
                                                {linesOfAct(a.id).map((l) => {
                                                    const inv = l.invoice_line
                                                        ? invoiceById.get(cd.invoiceLines.find((x) => x.id === l.invoice_line)?.invoice ?? '')
                                                        : undefined;
                                                    const item = l.item ? itemById.get(l.item) : undefined;
                                                    return (
                                                        <li key={l.id}>
                                                            {item?.title ?? 'позиция'}
                                                            {inv ? ` · счёт ${inv.number ?? shortId(inv.id)}` : ''}
                                                            {' · '}{fmtMoney(l.amount, a.currency)}
                                                        </li>
                                                    );
                                                })}
                                                {linesOfAct(a.id).length === 0 && <li className="muted">строк нет</li>}
                                            </ul>
                                            {a.status === 'issued' && (
                                                <DeliveryBlock act={a} delivery={deliveryByAct.get(a.id)}
                                                               canManage={canManage} canExempt={canReopen}
                                                               requireOriginal={requireOriginal}
                                                               onChanged={reload} />
                                            )}
                                        </td>
                                        <td className="nowrap">
                                            {canManage && a.status === 'draft' && (
                                                <button className="btn small" disabled={actionBusy}
                                                        onClick={() => void run(() => confirmAct(a.id, a.revision), `Акт ${a.number ?? ''} выставлен.`)}>
                                                    Подтвердить акт
                                                </button>
                                            )}
                                            {canManage && (a.status === 'draft' || a.status === 'issued') && (
                                                <button className="btn small" disabled={actionBusy}
                                                        onClick={() => setCancelFor(a)}>
                                                    Отменить акт
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                {cancelFor && (
                    <Card title={`Отмена акта ${cancelFor.number ?? shortId(cancelFor.id)}`}>
                        <ReasonForm title="Причина отмены акта" label="Причина отмены"
                                    submitLabel="Подтвердить отмену" onClose={() => setCancelFor(null)}
                                    onSubmit={async (reason) => {
                                        await cancelAct(cancelFor.id, { revision: cancelFor.revision, reason });
                                        reload();
                                    }} />
                    </Card>
                )}
            </Card>

            {canManage && (
                <Card title="Новый акт (частичное закрытие)">
                    {cd.invoiceLines.length === 0 ? (
                        <Empty text="Нет строк счетов по заявке — сначала зарегистрируйте счёт." />
                    ) : (
                        <NewActForm invoiceLines={cd.invoiceLines} invoices={cd.invoices}
                                    items={data.items} closedByLine={closedByLine} onDone={reload} />
                    )}
                </Card>
            )}

            <Card title="Чек-лист закрытия">
                <ChecklistBlock checklist={cd.checklist} />
            </Card>

            <Card title="Закрытие заявки">
                {!closed && canManage && (
                    <button className="btn primary" disabled={actionBusy}
                            onClick={() => void run(async () => {
                                await closeRequest(data.request.id, data.request.revision);
                                data.reload();
                            }, 'Заявка закрыта.')}>
                        Закрыть заявку
                    </button>
                )}
                {!closed && !canManage && (
                    <Empty text="Закрытие доступно ролям accountant/supervisor/admin." />
                )}
                {closed && (
                    <div>
                        <div className="alert alert-info">Заявка закрыта (стадия «Успешна»).</div>
                        {canReopen && !reopenOpen && (
                            <button className="btn" onClick={() => setReopenOpen(true)}>Повторно открыть</button>
                        )}
                        {reopenOpen && (
                            <ReasonForm title="Причина повторного открытия" label="Причина"
                                        submitLabel="Повторно открыть" onClose={() => setReopenOpen(false)}
                                        onSubmit={async (reason) => {
                                            await reopenRequest(data.request.id, { revision: data.request.revision, reason });
                                            data.reload();
                                            reload();
                                        }} />
                        )}
                    </div>
                )}
            </Card>
        </div>
    );
}
